<template>
  <main class="biblioteca">
    <section class="biblioteca__hero">
      <div class="biblioteca__container biblioteca__hero-grid">
        <div class="biblioteca__hero-copy">
          <DsText as="bebas2" class="biblioteca__hero-title">
            <span class="biblioteca__hero-title-line">
              <span class="biblioteca__hero-title-accent">Tu camino</span>
              <span class="biblioteca__hero-title-base">con HIR Casa</span>
            </span>
          </DsText>
          <DsText as="subtitle7" class="biblioteca__hero-subtitle">
            Encuentra la información que necesitas en un solo lugar.
          </DsText>
          <div class="biblioteca__hero-body">
            <DsText as="body3" class="biblioteca__hero-paragraph">
              Aquí encontrarás guías, documentos y recursos que te acompañarán en cada etapa de tu proceso con HIR Casa.
              <br>
              ¿No encuentras lo que buscas? Contáctanos por Chat o WhatsApp.
              <br class="biblioteca__hero-desktop-break">
              Con gusto te ayudaremos.
            </DsText>
          </div>
        </div>
        <picture class="biblioteca__hero-media">
          <source :srcset="heroMobile" media="(max-width: 767px)">
          <source :srcset="heroTablet" media="(max-width: 1024px)">
          <img :src="heroDesktop" alt="Familias y clientes celebrando su camino con HIR Casa" class="biblioteca__hero-image">
        </picture>
      </div>
    </section>
    <section class="biblioteca__search">
      <div class="biblioteca__container biblioteca__search-wrap">
        <div class="biblioteca__search-box">
          <span class="biblioteca__search-icon" aria-hidden="true">
            <img :src="searchIcon" alt="" class="biblioteca__search-icon-image">
          </span>

          <input
            v-model.trim="searchQuery"
            type="search"
            class="biblioteca__search-input"
            placeholder="Busca un artículo"
            @keydown.enter.prevent="openFirstSearchResult"
          >

          <div v-if="searchQuery && filteredSearchEntries.length" class="biblioteca__search-results">
            <button
              v-for="entry in filteredSearchEntries"
              :key="`${entry.label}-${entry.routeName}`"
              type="button"
              class="biblioteca__search-result"
              @click="goToRoute(entry.routeName)"
            >
              <span class="biblioteca__search-result-label">{{ entry.label }}</span>
              <span class="biblioteca__search-result-category">{{ entry.category }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="biblioteca__library">
      <div class="biblioteca__container">
        <div class="biblioteca__section-copy">
          <DsText as="bebas4" class="biblioteca__section-title">Encuentra lo que necesitas</DsText>
          <DsText as="body3" class="biblioteca__section-description">
            Explora cada sección y encuentra la información que preparamos especialmente para ti.
          </DsText>
        </div>

        <div class="biblioteca__grid">
          <BibliotecaTopicCard
            v-for="card in topicCards"
            :key="card.id"
            class="biblioteca__topic-card"
            :icon="card.icon"
            :title="card.title"
            :description="card.description"
            :mobile-description="card.mobileDescription"
            :mobile-description-breakpoint="card.mobileDescriptionBreakpoint"
            :items="card.items"
            :is-open="activeTopicCardId === card.id"
            @toggle-card="toggleTopicCard(card.id)"
          />
        </div>
      </div>
    </section>

    <DsContactHelp />
  </main>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import DsText from '../atoms/DsText.vue'
import DsContactHelp from '../components/DsContactHelp.vue'
import BibliotecaTopicCard from './BibliotecaTopicCard.vue'
import heroDesktop from '../../../assets/Biblioteca/biblioteca_hero_desktop.png'
import heroTablet from '../../../assets/Biblioteca/biblioteca_hero_tablet.webp'
import heroMobile from '../../../assets/Biblioteca/biblioteca_hero_mobile.png'
import welcomeIcon from '../../../assets/Biblioteca/biblioteca_handshake.svg'
import walletIcon from '../../../assets/Biblioteca/biblioteca_wallet.svg'
import shieldIcon from '../../../assets/Biblioteca/biblioteca_shield.svg'
import listIcon from '../../../assets/Biblioteca/biblioteca_list.svg'
import adjuIcon from '../../../assets/Biblioteca/biblioteca_gavel.svg'
import analisisIcon from '../../../assets/Biblioteca/biblioteca_waves.svg'
import escrituIcon from '../../../assets/Biblioteca/biblioteca_paper.svg'
import LiquidaIcon from '../../../assets/Biblioteca/biblioteca_cardHeart.svg'
import searchIcon from '../../../assets/Biblioteca/biblioteca_search.svg'
import { ClienteRouteNames } from "../../../router/routes";

interface SearchEntry {
  label: string
  category: string
  routeName: string
}

interface FinanceItem {
  title: string
  description: string
  routeName: string
}

interface TopicCard {
  id: string
  icon: string
  title: string
  description: string
  mobileDescription?: string
  mobileDescriptionBreakpoint?: 'mobile' | 'tablet'
  items: FinanceItem[]
}

const router = useRouter()
const searchQuery = ref('')
const activeTopicCardId = ref<string | null>(null)

const topicCards: TopicCard[] = [
  {
    id: 'bienvenida',
    icon: welcomeIcon,
    title: 'Bienvenida',
    description: 'Todo lo que necesitas para comenzar tu experiencia con HIR Casa.',
    items: [
      {
        title: 'Bienvenida',
        description: 'Conoce cómo empezar tu proceso.',
        routeName: ClienteRouteNames.materialApoyoBienvenida
      },
      {
        title: 'Estrena2',
        description: 'Conoce cómo empezar tu proceso.',
        routeName: ClienteRouteNames.materialApoyoEstrena2
      },
      {
        title: 'Hazte dueña',
        description: 'Conoce cómo empezar tu proceso.',
        routeName: ClienteRouteNames.materialApoyoHazteDuena
      },
      {
        title: 'Tu construye',
        description: 'Conoce cómo empezar tu proceso.',
        routeName: ClienteRouteNames.materialApoyoTuConstruye
      },
      {
        title: 'Preventa',
        description: 'Conoce cómo empezar tu proceso.',
        routeName: ClienteRouteNames.materialApoyoPreventa
      },
    ]
    },
      {
    id: 'pagos',
    icon: walletIcon,
    title: 'Pagos',
    description: 'Consulta opciones de pago, descuentos y servicios relacionados.',
    items: [
      {
        title: 'Pagos y Beneficios',
        description: 'Conoce las formas de realizar tus pagos.',
        routeName: ClienteRouteNames.materialApoyoPagos
      },
      {
        title: 'Descuentos',
        description: 'Consulta los descuentos disponibles.',
        routeName: 'cliente-cliente-material-apoyo-descuentos'
      },
      {
        title: 'Domiciliación',
        description: 'Automatiza el pago de tus mensualidades.',
        routeName: 'cliente-cliente-material-apoyo-domiciliacion'
      },
    ]
  },
  {
    id: 'seguros',
    icon: shieldIcon,
    title: 'Seguros',
    description: 'Conoce las coberturas, beneficios y protección de tu patrimonio.',
    mobileDescription: 'Conoce el proceso de adjudicación y todo lo relacionado.',
    mobileDescriptionBreakpoint: 'mobile',
    items: [
      {
        title: 'Seguros',
        description: 'Estamos para ayudarte cuando más lo necesitas ',
        routeName: ClienteRouteNames.materialApoyoSeguros
      }
    ]
  },
  {
    id: 'tramites',
    icon: listIcon,
    title: 'Trámites',
    description: 'Encuentra los trámites disponibles y cómo realizarlos.',
    items: [
      {
        title: 'Guía de trámites',
        description: 'Conoce los trámites disponibles y cómo realizarlos.',
        routeName: ClienteRouteNames.materialApoyoGuiaTramites
      },
      {
        title: 'De pagos',
        description: 'Realiza gestiones relacionadas con tus pagos.',
        routeName: 'cliente-cliente-material-apoyo-firma'
      },
      {
        title: 'De contratos',
        description: 'Consulta y solicita trámites de tu contrato.',
        routeName: 'cliente-cliente-material-apoyo-titulacion'
      },
      {
        title: 'Aclaraciones',
        description: 'Solicita una revisión o aclaración.',
        routeName: 'cliente-cliente-material-apoyo-liberacion'
      }
    ]
  },
  {
    id: 'adjucacion',
    icon: adjuIcon,
    title: 'Adjudicación',
    description: 'Descubre el proceso de adjudicación y los pasos a seguir.',
    items: [
      {
        title: 'Guía de adjudicatario',
        description: 'Conoce los pasos para adjudicar tu financiamiento.',
        routeName: ClienteRouteNames.materialApoyoGuiaDeAdjudicacion
      }
    ]
  },
  {
    id: 'analisis-financiero',
    icon: analisisIcon,
    title: 'Análisis financiero',
    description: 'Consulta información sobre tu evaluación y proceso financiero.',
    items: [
      {
        title: 'Entrevista',
        description: 'Prepárate para tu evaluación financiera.',
        routeName: 'cliente-cliente-material-apoyo-tramites'
      }
    ]
  },
  {
    id: 'escrituracion',
    icon: escrituIcon,
    title: 'Escrituración',
    description: 'Prepárate para la firma y escrituración de tu inmueble.',
    items: [
      {
        title: 'Avalúos',
        description: 'Conoce el proceso y requisitos del avalúo.',
        routeName: ClienteRouteNames.materialApoyoAvaluos
      },
      {
        title: 'Firma de escritura',
        description: 'Todo listo para firmar tu escritura.',
        routeName: ClienteRouteNames.materialApoyoFirmaEscrituras
      }
    ]
  },
  {
    id: 'liquidacion',
    icon: LiquidaIcon,
    title: 'Liquidación',
    description: 'Revisa el proceso para liquidar tu financiamiento o cancelar tu hipoteca.',
    mobileDescription: 'Encuentra respuestas, documentos y nuestros canales de atención.',
    mobileDescriptionBreakpoint: 'tablet',
    items: [
      {
        title: 'Liquidación de hipoteca',
        description: 'Es hora de disfrutar de tu nuevo inmueble y celebrar todo lo que has logrado.',
        routeName: ClienteRouteNames.materialApoyoLiquidacionDeHipoteca
      },
      {
        title: 'Liberación y cancelación de hipoteca',
        description: '¡Libérate de la hipoteca y toma las llaves de tu patrimonio!',
        routeName: ClienteRouteNames.materialApoyoLiberacionCancelacion
      }
    ]
  }
]

const searchEntries: SearchEntry[] = [
  { label: 'Mi financiamiento', category: 'Biblioteca', routeName: 'cliente-cliente-material-apoyo-pagos' },
  { label: 'Pagos', category: 'Mi financiamiento', routeName: 'cliente-cliente-material-apoyo-pagos' },
  { label: 'Domiciliación', category: 'Mi financiamiento', routeName: 'cliente-cliente-material-apoyo-domiciliacion' },
  { label: 'Descuentos', category: 'Mi financiamiento', routeName: 'cliente-cliente-material-apoyo-descuentos' },
  { label: 'Factor de actualización', category: 'Mi financiamiento', routeName: 'cliente-cliente-material-apoyo-factor-actualizacion' },
  { label: 'Liquidación', category: 'Mi financiamiento', routeName: 'cliente-cliente-material-apoyo-liquidacion' },
  { label: 'Remanentes de monto adjudicado', category: 'Mi financiamiento', routeName: 'cliente-cliente-material-apoyo-remanentes' },
  { label: 'Proceso de adjudicación', category: 'Biblioteca', routeName: 'cliente-cliente-material-apoyo-adjudicacion' },
  { label: 'Producto Preventas', category: 'Proceso de adjudicación', routeName: ClienteRouteNames.materialApoyoPreventa },
  { label: 'Adjudicación', category: 'Proceso de adjudicación', routeName: 'cliente-cliente-material-apoyo-adjudicacion' },
  { label: 'Guía de adjudicación', category: 'Proceso de adjudicación', routeName: 'cliente-cliente-material-apoyo-guia' },
  { label: 'Avalúos', category: 'Proceso de adjudicación', routeName: 'cliente-cliente-material-apoyo-avaluos' },
  { label: 'Protección', category: 'Biblioteca', routeName: 'cliente-cliente-material-apoyo-seguros' },
  { label: 'Seguros', category: 'Protección', routeName: 'cliente-cliente-material-apoyo-seguros' },
  { label: 'Escrituración', category: 'Biblioteca', routeName: 'cliente-cliente-material-apoyo-firma' },
  { label: 'Firma de escritura', category: 'Escrituración', routeName: 'cliente-cliente-material-apoyo-firma' },
  { label: 'Titulación', category: 'Escrituración', routeName: 'cliente-cliente-material-apoyo-titulacion' },
  { label: 'Liberación de hipoteca', category: 'Escrituración', routeName: 'cliente-cliente-material-apoyo-liberacion' },
  { label: 'Ayuda y recursos', category: 'Biblioteca', routeName: 'cliente-cliente-material-apoyo-contacto' },
  { label: 'Bienvenida', category: 'Ayuda y recursos', routeName: 'cliente-cliente-material-apoyo-bienvenida' },
  { label: 'Tips para el vendedor de tu inmueble', category: 'Ayuda y recursos', routeName: 'cliente-cliente-material-apoyo-venta' },
  { label: 'Trámites de tu contrato', category: 'Biblioteca', routeName: 'cliente-cliente-material-apoyo-tramites' },
  { label: 'Trámites', category: 'Trámites de tu contrato', routeName: 'cliente-cliente-material-apoyo-tramites' },
  { label: 'Aclaraciones', category: 'Trámites de tu contrato', routeName: 'cliente-cliente-material-apoyo-aclaraciones' }
]

const filteredSearchEntries = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()

  if (!query) {
    return []
  }

  return searchEntries
    .filter(entry => entry.label.toLowerCase().includes(query) || entry.category.toLowerCase().includes(query))
    .slice(0, 8)
})

function goToRoute(routeName: string) {
  searchQuery.value = ''
  router.push({ name: routeName })
}

function openFirstSearchResult() {
  if (filteredSearchEntries.value.length) {
    goToRoute(filteredSearchEntries.value[0].routeName)
  }
}

function toggleTopicCard(cardId: string) {
  activeTopicCardId.value = activeTopicCardId.value === cardId ? null : cardId
}

</script>

<style scoped>
.biblioteca {
  background: #ffffff;
  color: #111111;
  padding: 48px 0 0 0;
}

.biblioteca__container {
  width: min(100%, 1180px);
  margin: 0 auto;
  padding-left: 0;
  padding-right: 0;
  box-sizing: border-box;
}

.biblioteca__hero {
  padding: 48px 1.5rem 5rem;
}

.biblioteca__hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 380px);
  align-items: center;
  gap: 56px;
}

.biblioteca__hero-copy {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 564px;
}

.biblioteca__hero-title {
  display: block;
  color: var(--ds-purple-900);
  font-size: 72px;
  font-weight: 700;
  line-height: 100%;
}

.biblioteca__hero-title-line {
  display: inline-flex;
  align-items: baseline;
  gap: 0 12px;
  white-space: nowrap;
}

.biblioteca__hero-title-accent {
  color: var(--ds-purple-400);
}

.biblioteca__hero-title-base {
  color: var(--ds-purple-900);
}

.biblioteca__hero-subtitle {
  color: var(--ds-purple-900);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
}

.biblioteca__hero-body {
  display: flex;
  flex-direction: column;
}

.biblioteca__hero-paragraph {
  color: #111111;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  max-width: 56ch;
}

.biblioteca__hero-desktop-break {
  display: none;
}

.biblioteca__hero-media {
  display: block;
}

.biblioteca__hero-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 24px;
}

@media (min-width: 1208px) {
  .biblioteca__hero-desktop-break {
    display: block;
  }

  .biblioteca__hero-grid {
    grid-template-columns: minmax(0, 1fr) 568px;
    gap: 48px;
  }

  .biblioteca__hero-media {
    width: 568px;
    height: 400px;
    justify-self: end;
  }

  .biblioteca__hero-image {
    width: 568px;
    height: 400px;
    object-fit: cover;
    border-radius: 24px;
  }
}

.biblioteca__search {
  padding: 0 1.5rem 5rem;
}

.biblioteca__search-wrap {
  display: flex;
  justify-content: center;
}

.biblioteca__search-box {
  width: min(100%, 720px);
  position: relative;
}

.biblioteca__search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.biblioteca__search-icon-image {
  width: 20px;
  height: 20px;
  display: block;
}

.biblioteca__search-input {
  width: 100%;
  min-height: 54px;
  border: 1px solid #d9dced;
  border-radius: 10px;
  padding: 14px 18px 14px 48px;
  color: #111111;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  box-sizing: border-box;
  outline: none;
}

.biblioteca__search-input::placeholder {
  color: #555555;
}

.biblioteca__search-input:focus {
  border-color: rgba(73, 47, 229, 0.45);
  box-shadow: 0 0 0 4px rgba(73, 47, 229, 0.08);
}

.biblioteca__search-results {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 8px);
  z-index: 5;
  background: #ffffff;
  border: 1px solid #e5e7f2;
  border-radius: 12px;
  box-shadow: 0 18px 40px rgba(31, 20, 96, 0.12);
  overflow: hidden;
}

.biblioteca__search-result {
  width: 100%;
  padding: 14px 18px;
  border: 0;
  border-top: 1px solid #eef0f7;
  background: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  text-align: left;
  cursor: pointer;
}

.biblioteca__search-result:first-child {
  border-top: 0;
}

.biblioteca__search-result:hover {
  background: #fafaff;
}

.biblioteca__search-result-label {
  color: var(--ds-purple-900);
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 600;
}

.biblioteca__search-result-category {
  color: #666666;
  font-family: "Inter", sans-serif;
  font-size: 12px;
  font-weight: 400;
}

.biblioteca__library {
  padding: 0 1.5rem 9rem;
}

.biblioteca__section-copy {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 5rem;
}

.biblioteca__section-title {
  color: var(--ds-purple-900);
  font-size: 32px;
  font-weight: 700;
  line-height: 1;
}

.biblioteca__section-description {
  color: #111111;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.45;
}

.biblioteca__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 325px));
  justify-content: center;
  gap: 30px 30px;
  align-items: start;
  isolation: isolate;
}

.biblioteca__topic-card {
  align-self: start;
}

@media (min-width: 768px) and (max-width: 1024px) {
  .biblioteca__container {
    padding-left: 24px;
    padding-right: 24px;
  }

  .biblioteca__hero {
    padding: 16px 16px;
    padding-bottom: 40px;
  }

  .biblioteca__hero-grid {
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: 32px;
  }

  .biblioteca__hero-copy {
    max-width: none;
  }

  .biblioteca__hero-media {
    width: 100%;
    height: 400px;
    justify-self: end;
  }

  .biblioteca__hero-image {
    width: 100%;
    height: 400px;
    object-fit: cover;
    object-position: center;
    border-radius: 24px;
  }

  .biblioteca__hero-title {
    font-size: 72px;
  }

  .biblioteca__hero-title-line {
    display: inline;
    white-space: normal;
  }

  .biblioteca__hero-title-base {
    display: block;
  }

  .biblioteca__hero-paragraph {
    max-width: 29ch;
  }

  .biblioteca__grid {
    grid-template-columns: repeat(2, minmax(0, 100%));
    justify-content: start;
    gap: 18px 16px;
  }
  .biblioteca__search-box {
    width: 100%;
    position: relative;
  }
}

@media (max-width: 767px) {
  .biblioteca__hero-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .biblioteca__hero-copy {
    order: 2;
    max-width: none;
  }

  .biblioteca__container {
    padding-left: 16px;
    padding-right: 16px;
  }

  .biblioteca__hero {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  .biblioteca__hero-copy {
    gap: 20px;
  }

  .biblioteca__hero-title {
    font-size: 40px;
  }

  .biblioteca__hero-title-line {
    display: inline-flex;
    flex-wrap: wrap;
    gap: 0 8px;
  }

  .biblioteca__hero-title-base {
    display: inline;
  }

  .biblioteca__hero-media {
    order: 1;
    width: 100%;
    max-width: none;
    justify-self: stretch;
  }

  .biblioteca__hero-subtitle {
    font-size: 16px;
  }

  .biblioteca__hero-paragraph {
    font-size: 16px;
    max-width: 29ch;
  }

  .biblioteca__hero-image {
    width: 100%;
    height: auto;
    object-fit: cover;
    object-position: center;
  }

  .biblioteca__search {
    padding-bottom: 32px;
  }

  .biblioteca__search-input {
    min-height: 50px;
    font-size: 15px;
  }

  .biblioteca__section-title {
    font-size: 32px;
  }

  .biblioteca__section-description {
    font-size: 14px;
  }

  .biblioteca__grid {
    grid-template-columns: minmax(0, 100%);
    justify-content: start;
    gap: 14px;
  }

  .biblioteca__library {
    padding: 0 0 5rem;
  }

}
</style>
